import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import ReactTooltip from 'react-tooltip'
import Legend from './Legend'
import Table from './Table'
import TableMobile from './TableMobile'
import AddNoteModal from './AddNoteModal'
import LoadingSpinner from '../common/LoadingSpinner'
import SearchWithFilter from '../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../common/FiltersModal'
import SimpleButton from '../common/SimpleButton'
import { toast } from '../common/Toast'
import EmptyPageResults from '../common/EmptyPageResults'
import { RefreshIcon } from '../../icons'
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect'
import { loadSterilizationReports, loadSterilizationReportsCount } from '../../actions/reports'
import { getSterilizationReportsCsv } from '../../utils/requests/usersAPI'
import { hasInstrumentTrackingPermission } from '../../utils/permissionValidation'
import { downloadFile, isG4orG4PlusProduct } from '../../utils/functions'
import { keyboardEvents } from '../../utils/keyboardEvents'
import { useWindowSize } from '../../libs/hooks'
import { trackEvent, gaActions, gaCategories } from '../../utils/ga4'
import './reports.scss'

function Sterilization({ userProducts, apertures, ...props }) {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const canAccessInstrumentTracking = hasInstrumentTrackingPermission(props.userPermissions)
  const [reports, setReports] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [modelsWithSerialNumbers, setModelsWithSerialNumbers] = useState([])
  const [selectedFilterStartDate, setSelectedFilterStartDate] = useState(null)
  const [selectedFilterEndDate, setSelectedFilterEndDate] = useState(null)
  const [selectedFilterModelsWithSerialNumbers, setSelectedFilterModelsWithSerialNumbers] = useState([])
  const [addNoteIdentifier, setAddNoteIdentifier] = useState(null)
  const [isTableVisible, setTableVisibility] = useState(false)
  const [showSpinner, setShowSpinner] = useState(null)
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)

  useEffect(() => {
    if (userProducts && Object.keys(userProducts).length > 0) {
      const modelsWithSerialNumbers = Object.values(userProducts)
        .filter((item) => item.model && item.model.trim().length > 0)
        .filter((item) => isG4orG4PlusProduct(item))
        .filter((item) => !item.model.toLowerCase().includes('canalpro jeni evo'))
        .reduce((acc, item) => {
          if (!acc[item.model]) {
            acc[item.model] = {
              id: item.model,
              name: item.model,
              children: [],
            }
          }

          acc[item.model].children.push({
            id: item.serial_number,
            name: item.serial_number,
          })

          return acc
        }, {})

      const mappedModelsWithSerialNumbers = Object.values(modelsWithSerialNumbers).map((item) => ({ ...item }))

      setModelsWithSerialNumbers(mappedModelsWithSerialNumbers)
    }
  }, [userProducts])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    setReports(props.reports)
  }, [props.reports])

  useEffect(() => {
    handleLoadReports()
  }, [currentPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(() => {
      if (shouldApplyFilters) {
        if (currentPage === 1 && !isFirstLoad) {
          handleLoadReports()
        } else {
          setCurrentPage(1)
        }
      }

      setShouldApplyFilters(true)
    }, 300)
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [selectedFilterStartDate, selectedFilterEndDate, selectedFilterModelsWithSerialNumbers])

  const handleLoadReports = () => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.instrumentReprocessingReportsPage,
      label: 'Instrument Reprocessing Reports Page: Load Reports',
    })

    const thiryMinutes = 1800000

    let reportQueryParams = {
      page: currentPage,
      limit: 20,
    }

    if (selectedFilterStartDate) {
      reportQueryParams = { ...reportQueryParams, start_date: convertDate(selectedFilterStartDate) }
    }

    if (selectedFilterEndDate) {
      reportQueryParams = { ...reportQueryParams, end_date: convertDate(selectedFilterEndDate) }
    }

    if (selectedFilterModelsWithSerialNumbers.length > 0) {
      const models = []
      const serialNumbers = []
      for (let idx = 0; idx < selectedFilterModelsWithSerialNumbers.length; idx++) {
        models.push(selectedFilterModelsWithSerialNumbers[idx].id)

        const currentModelSerialNumbers = selectedFilterModelsWithSerialNumbers[idx].children?.map((serialNumber) => serialNumber) ?? []
        serialNumbers.push(...currentModelSerialNumbers)
      }

      reportQueryParams = { ...reportQueryParams, model: models, serialNumber: serialNumbers }
    }

    props.actions.loadSterilizationReports(props.cognitoSub, reportQueryParams).then(() => {
      setTableVisibility(true)
      setIsFirstLoad(false)
    })

    const queryParamsWithoutPaginationProps = { ...reportQueryParams }
    delete queryParamsWithoutPaginationProps.page
    delete queryParamsWithoutPaginationProps.limit

    const lastReportQueryParams = { ...props.lastReportsQueryParams }
    delete lastReportQueryParams.cacheTime
    if (
      Object.keys(props.lastReportsQueryParams).length === 0 ||
      JSON.stringify(queryParamsWithoutPaginationProps) !== JSON.stringify(lastReportQueryParams) ||
      new Date().getTime() - props.lastReportsQueryParams.cacheTime > thiryMinutes
    ) {
      props.actions.loadSterilizationReportsCount(props.cognitoSub, queryParamsWithoutPaginationProps)
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleResetFilters = () => {
    setSelectedFilterModelsWithSerialNumbers([])
    setSelectedFilterStartDate(null)
    setSelectedFilterEndDate(null)
  }

  const convertDate = (date) => {
    return date?.split('T')[0]?.replace(/-/g, '') || ''
  }

  const countSelectedModelsWithSerialNumbers = () => {
    let count = 0

    for (let idx = 0; idx < selectedFilterModelsWithSerialNumbers.length; idx++) {
      count += selectedFilterModelsWithSerialNumbers[idx].children?.length + 1 ?? 1
    }

    return count
  }

  const handleOpenAddNote = (serialNumber, cycleFault, isG4Plus, fileName, date, id, modelId, pedSource) => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.instrumentReprocessingReportsPage,
      label: 'Instrument Reprocessing Reports Page: Add Note',
    })

    setAddNoteIdentifier({
      serialNumber,
      cycleFault,
      isG4Plus,
      fileName,
      date,
      pedSource,
      modelId,
      id,
    })
  }

  const handleSucessfullAddNote = (identifier, note) => {
    const updatedReports = reports.map((report) => {
      if (report.id === identifier?.id) {
        return { ...report, notes: [...report.notes, note] }
      }

      return report
    })

    setReports(updatedReports)
  }

  const handleDownloadReports = () => {
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.instrumentReprocessingReportsPage,
      label: 'Instrument Reprocessing Reports Page: CSV Download',
    })

    let queryParams = {
      limit: 1000,
      page: 1,
    }

    if (selectedFilterStartDate) {
      queryParams = { ...queryParams, start_date: convertDate(selectedFilterStartDate) }
    }

    if (selectedFilterEndDate) {
      queryParams = { ...queryParams, end_date: convertDate(selectedFilterEndDate) }
    }

    if (selectedFilterModelsWithSerialNumbers.length > 0) {
      const models = []
      const serialNumbers = []
      for (let idx = 0; idx < selectedFilterModelsWithSerialNumbers.length; idx++) {
        models.push(selectedFilterModelsWithSerialNumbers[idx].id)

        const currentModelSerialNumbers = selectedFilterModelsWithSerialNumbers[idx].children?.map((serialNumber) => serialNumber) ?? []
        serialNumbers.push(...currentModelSerialNumbers)
      }

      queryParams = { ...queryParams, model: models, serialNumber: serialNumbers }
    }

    setShowSpinner(true)

    getSterilizationReportsCsv(props.cognitoSub, queryParams)
      .then((res) => {
        const date = new Date().toISOString().split('T')
        const dateNow = date[0].split('-').reverse().join('_')
        const timeNow = date[1].split('.')[0].split(':').join('_')

        downloadFile({
          data: res,
          fileName: `sterilization-reports_${dateNow}_${timeNow}.csv`,
          fileType: 'text/csv',
        })

        setShowSpinner(false)

        toast.success(t('reports.download_success'))
      })
      .catch(() => {
        setShowSpinner(false)

        toast.error(t('error.failure_msg'))
      })
  }

  const handleShowFiltersModal = () => {
    setShowFiltersModal(true)
  }

  return (
    <div className="reports-wrapper">
      <div className="actions-wrapper">
        <SearchWithFilter
          showFiltersModal={handleShowFiltersModal}
          onResetFilters={() => handleResetFilters()}
          showCounterBadge={selectedFilterStartDate || selectedFilterEndDate || selectedFilterModelsWithSerialNumbers.length > 0}
          counterBadge={countSelectedModelsWithSerialNumbers() + (selectedFilterStartDate || selectedFilterEndDate ? 1 : 0)}
        />
        <div className="buttons-wrapper">
          <div
            tabIndex={0}
            role="button"
            className="refresh-icon"
            onClick={handleLoadReports}
            onKeyDown={(e) => e.key === keyboardEvents.ENTER && handleLoadReports()}
          >
            <RefreshIcon data-tip={t('enhanced_event_log.refresh')} />
            <ReactTooltip place="top" type="dark" effect="solid" />
          </div>
          <SimpleButton className="action-button" onClick={() => handleDownloadReports()} disabled={showSpinner || !reports?.length > 0}>
            {t('download')}
          </SimpleButton>
        </div>
      </div>

      {isTableVisible && (
        <>
          {reports.length > 0 ? (
            <>
              <Legend />
              {!isMobile ? (
                <Table
                  reports={reports}
                  userProducts={userProducts}
                  totalReports={props.totalReports}
                  currentPage={currentPage}
                  canAccessInstrumentTracking={canAccessInstrumentTracking}
                  onChangeCurrentPage={handlePageChange}
                  handleOpenAddNote={handleOpenAddNote}
                  setShowSpinner={setShowSpinner}
                  isLoading={showSpinner}
                />
              ) : (
                <TableMobile
                  reports={reports}
                  userProducts={userProducts}
                  totalReports={props.totalReports}
                  currentPage={currentPage}
                  canAccessInstrumentTracking={canAccessInstrumentTracking}
                  onChangeCurrentPage={handlePageChange}
                  handleOpenAddNote={handleOpenAddNote}
                  setShowSpinner={setShowSpinner}
                  isLoading={showSpinner}
                />
              )}
            </>
          ) : (
            <EmptyPageResults title={t('no_data_found')} />
          )}
        </>
      )}
      {addNoteIdentifier && (
        <AddNoteModal
          userUuid={props.cognitoSub}
          identifier={addNoteIdentifier}
          onSuccesfullAddNote={handleSucessfullAddNote}
          closeModal={() => setAddNoteIdentifier(null)}
          setShowSpinner={setShowSpinner}
        />
      )}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('date'),
              type: FilterType.dateRange,
              dataset: null,
              input: { startDate: selectedFilterStartDate, endDate: selectedFilterEndDate },
              output: (startDate, endDate) => {
                setSelectedFilterStartDate(startDate)
                setSelectedFilterEndDate(endDate)
              },
              withStartDateOnly: true,
              hasMinDate: true,
            },
            {
              name: t('reports.model_and_serial_number'),
              type: FilterType.multiSelectWithChildren,
              dataset: modelsWithSerialNumbers,
              input: selectedFilterModelsWithSerialNumbers,
              output: (filterModelsWithSerialNumbers) => {
                setSelectedFilterModelsWithSerialNumbers(filterModelsWithSerialNumbers)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function mapStateToProps({ authedUser, userProducts, apertures, userPermissions, reports }) {
  return {
    cognitoSub: authedUser?.cognitoSub,
    userProducts: userProducts?.products || {},
    apertures: apertures?.apertures || [],
    userPermissions,
    reports: reports.reports || [],
    totalReports: reports.totalReports || 0,
    lastReportsQueryParams: reports.lastReportsQueryParams || {},
    isLoading: reports.isLoading,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadSterilizationReports,
        loadSterilizationReportsCount,
      },
      dispatch,
    ),
  }
}

export default connect(mapStateToProps, dispatchToProps)(Sterilization)

